import lookTables from '../data/skin_render/look_tables.json';
import { CHARACTER_SEX } from '../core/Build';

export const MAX_CACHE_ENTRIES = 40;
export const PERSISTED_RENDER_MAX_SIDE = 360;
export const PERSISTED_RENDER_WEBP_QUALITY = 0.85;
/** Skeleton id in Dofus Look strings ({bone|skins|...}); not the breed/class id. */
export const PLAYER_LOOK_BONE_ID = 1;

const BASE_BODY_COLORS = [
    15047528,
    7814953,
    14185518,
    4809554,
    5319189,
    5984835,
];

const DEFAULT_COLORS_BY_BREED = {
    1: { 0: [15047528, 7814953, 14185518, 4809554, 5319189, 5984835], 1: [15047528, 7814953, 14185518, 4809554, 5319189, 5984835] },
    2: { 0: [14325094, 14732959, 3572096, 4800822, 16755757, 12495223], 1: [14325094, 14732959, 3572096, 4800822, 16755757, 12495223] },
    3: { 0: [13732958, 15851694, 3093806, 14325820, 7814953, 9995877], 1: [16297081, 15588265, 3159600, 14517248, 7814953, 15588265] },
    4: { 0: [14206857, 15392448, 3435918, 6177568, 10715217, 15066306], 1: [14325094, 15392448, 3435918, 6177568, 3435918, 15066306] },
    5: { 0: [8550749, 15782577, 1923718, 6700064, 15042816, 15042816], 1: [8550749, 14533780, 1923718, 6700064, 15042816, 12499369] },
    6: { 0: [14732959, 15173176, 15690532, 5478807, 7687730, 14648576], 1: [14732959, 15173176, 15690532, 5478807, 7687730, 14648576] },
    7: { 0: [14325094, 7814953, 13997612, 4739655, 6833937, 10907934], 1: [15047528, 14996664, 14368563, 14471352, 9799968, 9791002] },
    8: { 0: [15704172, 15301153, 14736065, 12200764, 484225, 12429686], 1: [15047528, 15301153, 14736065, 12200764, 12200764, 12038292] },
    9: { 0: [15047528, 14383411, 7696171, 8675096, 9392643, 16422159], 1: [15047528, 14383411, 7696171, 9392643, 9392643, 16422159] },
    10: { 0: [9329441, 7966996, 15914657, 7820329, 15914657, 15914657], 1: [12282670, 7966996, 14994072, 10711608, 15258276, 15258276] },
    11: { 0: [15047528, 15896377, 8287530, 8727092, 16422159, 7750188], 1: [15047528, 15896377, 8287530, 8727092, 16422159, 7750188] },
    12: { 0: [13418918, 5329968, 9904435, 3423523, 14533780, 9904435], 1: [13418918, 5329968, 9904435, 9904435, 14533780, 9904435] },
    13: { 0: [15047528, 1341042, 3883833, 15703040, 7098924, 12499369], 1: [15047528, 1341042, 3883833, 15703040, 7098924, 12499369] },
    14: { 0: [15572842, 9836073, 4863523, 1855824, 11839343, 14077359], 1: [15572842, 9836073, 4863523, 1855824, 11839343, 14077359] },
    15: { 0: [14785388, 15060409, 3882547, 11430949, 10984301, 8100502], 1: [14785388, 15060409, 3882547, 11430949, 10984301, 8100502] },
    16: { 0: [16112841, 10072765, 8211000, 16765866, 2388929, 12957582], 1: [16112841, 10072765, 8211000, 16765866, 2388929, 12957582] },
    17: { 0: [16104582, 16439198, 2663850, 15071441, 16643820, 16755757], 1: [16104582, 16439198, 2663850, 13426363, 15987438, 16755757] },
    18: { 0: [9795658, 9989653, 8077087, 4081977, 12868619, 16032267], 1: [9598537, 13012508, 8077087, 4081977, 9717256, 16756535] },
    20: { 0: [15047528, 7814953, 4276545, 15325098, 8885653, 4209983], 1: [15047528, 7814953, 4276545, 15325098, 8885653, 4209983] },
};

const DARK_GREY = { r: 35, g: 36, b: 38 };

/** The one appearance rendered per class and sex. Defined in the backend
 *  exporter (core/services/skin_render/export_look_tables.py) because it decides
 *  which head/body skins get extracted; shipped here inside look_tables.json so
 *  there is a single source of truth. Numeric sex indexes work against the JSON's
 *  string keys via normal property coercion. */
export const CLASS_RENDER_PROFILES = lookTables.profiles;

export function normalizeSex(raw) {
    return Number(raw) === CHARACTER_SEX.FEMALE ? CHARACTER_SEX.FEMALE : CHARACTER_SEX.MALE;
}

function getDefaultColors(profile, sex) {
    return DEFAULT_COLORS_BY_BREED[profile?.breed]?.[sex] || BASE_BODY_COLORS;
}

function clamp255(value) {
    return Math.max(0, Math.min(255, Math.round(value)));
}

function rgbToInt({ r, g, b }) {
    return (clamp255(r) << 16) + (clamp255(g) << 8) + clamp255(b);
}

function intToRgb(value) {
    return {
        r: (value >> 16) & 255,
        g: (value >> 8) & 255,
        b: value & 255,
    };
}

function mixRgb(from, to, ratio) {
    return {
        r: from.r + (to.r - from.r) * ratio,
        g: from.g + (to.g - from.g) * ratio,
        b: from.b + (to.b - from.b) * ratio,
    };
}

export function buildCharacterColors(profile, sex, accentRgb = null) {
    const defaultColors = getDefaultColors(profile, sex);
    if (!accentRgb) return [...defaultColors];

    const skinColor = intToRgb(defaultColors[0]);
    const hairColor = intToRgb(defaultColors[1]);
    const primary = mixRgb(accentRgb, { r: 255, g: 255, b: 255 }, 0.12);
    const secondary = mixRgb(accentRgb, { r: 0, g: 0, b: 0 }, 0.28);

    return [
        rgbToInt(skinColor),
        rgbToInt(hairColor),
        rgbToInt(primary),
        rgbToInt(secondary),
        rgbToInt(DARK_GREY),
        rgbToInt(DARK_GREY),
    ];
}

